import { Stack } from '@mantine/core';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { TAPIKeyScope } from '@feathr/blackbox';
import { Checkbox, FormElement } from '@feathr/components';
import { useId } from '@feathr/hooks';

import ScopeGroupRow from './ScopeGroupRow';
import { getAllScopes, getResourceGroups, getTriStateValue } from './ScopePicker.utils';

import * as styles from './ScopePicker.css';

export interface IScopePickerProps {
  selected: Set<TAPIKeyScope>;
  onChange: (next: Set<TAPIKeyScope>) => void;
}

function ScopePicker({ onChange, selected }: Readonly<IScopePickerProps>): JSX.Element {
  const { t } = useTranslation();
  const labelId = useId();
  const groups = getResourceGroups();
  const allScopes = getAllScopes();

  const allSelected = allScopes.every((scope) => selected.has(scope));
  const someSelected = allScopes.some((scope) => selected.has(scope));
  const allValue = getTriStateValue(allSelected, someSelected);

  function handleToggleAll(checked?: boolean): void {
    onChange(checked ? new Set<TAPIKeyScope>(allScopes) : new Set<TAPIKeyScope>());
  }

  return (
    <FormElement
      aria-labelledby={labelId}
      className={styles.root}
      helpText={t('Scopes define the access for personal keys.')}
      label={t('Scopes')}
      labelId={labelId}
      role={'group'}
    >
      <Stack gap={'sm'}>
        <Checkbox label={t('All')} onChange={handleToggleAll} value={allValue} />

        {groups.map((group) => (
          <ScopeGroupRow
            group={group}
            key={group.resource}
            onChange={onChange}
            selected={selected}
          />
        ))}
      </Stack>
    </FormElement>
  );
}

export default ScopePicker;
